/** Hourly device-bound entitlement (spec section 2 "Login and entitlement lifecycle"). */
import { z } from 'zod';
import { AccountIdSchema, DeviceIdSchema, UserIdSchema } from '../ids.js';
import { CapabilitySchema } from '../roles.js';
import { CompactJwsSchema, IsoDateTimeSchema, SemVerSchema, defineRoute } from './common.js';

export const ENTITLEMENT_TTL_SECONDS = 60 * 60;
/** Renewal begins at ~50 minutes so bounded retry fits before expiry. */
export const ENTITLEMENT_RENEW_AFTER_SECONDS = 50 * 60;

export const EntitlementClaimsSchema = z.object({
  /** User id. */
  sub: UserIdSchema,
  deviceId: DeviceIdSchema,
  accountIds: z.array(AccountIdSchema),
  capabilities: z.array(CapabilitySchema),
  policyVersion: z.number().int().positive(),
  minAppVersion: SemVerSchema,
  /** Account ids where Rumi is disabled by an administrator. */
  rumiDisabledAccountIds: z.array(AccountIdSchema).default([]),
  /**
   * local-accounts change: accounts are authorised by local existence, not by `accountIds` (which the
   * server may still send, possibly empty). Always `local`; present so the model is explicit in the lease.
   */
  accountScope: z.literal('local').default('local'),
  iss: z.string(),
  aud: z.literal('agency-desktop'),
  jti: z.string(),
  iat: z.number().int(),
  exp: z.number().int(),
});
export type EntitlementClaims = z.infer<typeof EntitlementClaimsSchema>;

export const EntitlementRenewRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    appVersion: SemVerSchema,
    osVersion: z.string().max(40).optional(),
    /** Client clock, so the server can flag rollback. */
    clientTime: IsoDateTimeSchema,
    /** Content bundle version currently active on the device. */
    contentVersion: SemVerSchema.optional(),
  })
  .strict();
export type EntitlementRenewRequest = z.infer<typeof EntitlementRenewRequestSchema>;

export const EntitlementRenewResponseSchema = z.object({
  /** EdDSA compact JWS; verify against /.well-known/jwks.json. */
  entitlement: CompactJwsSchema,
  expiresAt: IsoDateTimeSchema,
  payload: EntitlementClaimsSchema,
  serverTime: IsoDateTimeSchema,
  /** A newer content release is available for this device's cohort. */
  contentUpdateAvailable: z.boolean(),
});
export type EntitlementRenewResponse = z.infer<typeof EntitlementRenewResponseSchema>;

export const entitlementRoutes = {
  'entitlements.renew': defineRoute({
    id: 'entitlements.renew',
    method: 'POST',
    path: '/v1/entitlements',
    auth: 'desktop',
    requestSchema: EntitlementRenewRequestSchema,
    responseSchema: EntitlementRenewResponseSchema,
    description: 'Issue a signed one-hour capability lease bound to the device. Denies suspended users and revoked devices.',
  }),
} as const;
